/**
 * ItemList Helpers - Funzioni di utilità per ItemList
 * Estratte da ItemList.tsx per migliorare la leggibilità
 */

import { DISTANCE_RANGES, ADMIN_LECCE_COORDS } from '../config/filters';
import { getAdminCoords } from '../config/geoConfig';
import { City } from '../types';

export interface ItemFilters {
  brand: string;
  model: string;
  min_price: string;
  max_price: string;
  year: string;
  min_length: string;
  max_length: string;
  engine_type: string;
  fuel_type: string;
  condition: string;
  min_hours: string;
  max_hours: string;
  horsepower: string;
  boat_type_code: string;
}

export const DEFAULT_FILTERS: ItemFilters = {
  brand: '',
  model: '',
  min_price: '',
  max_price: '',
  year: '',
  min_length: '',
  max_length: '',
  engine_type: '',
  fuel_type: '',
  condition: '',
  min_hours: '',
  max_hours: '',
  horsepower: '',
  boat_type_code: '',
};

export interface LocationCoords {
  lat: number;
  lng: number;
  source: string;
}

export const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Più recenti' },
  { value: 'created_asc', label: 'Meno recenti' },
  { value: 'price_asc', label: 'Prezzo ↑' },
  { value: 'price_desc', label: 'Prezzo ↓' },
  { value: 'year_desc', label: 'Anno ↓' },
  { value: 'length_desc', label: 'Lunghezza ↓' },
  { value: 'distance_asc', label: 'Distanza' },
];

export const DEFAULT_RADIUS_STEPS = DISTANCE_RANGES;

export const toFiniteCoord = (value: any): number => {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

export const getDealerCoords = (user: any): { lat: number; lng: number } | null => {
  const lat = toFiniteCoord(user?.lat);
  const lng = toFiniteCoord(user?.lng);
  if (lat !== 0 && lng !== 0) {
    return { lat, lng };
  }
  return null;
};

export const calcDistance = (fromLat: number, fromLng: number, toLat: number, toLng: number): number => {
  const R = 6371;
  const dLat = (toLat - fromLat) * Math.PI / 180;
  const dLng = (toLng - fromLng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(fromLat * Math.PI / 180) * Math.cos(toLat * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
};

export const calcDistFromLecce = (lat: number, lng: number): number => {
  return calcDistance(ADMIN_LECCE_COORDS.lat, ADMIN_LECCE_COORDS.lng, lat, lng);
};

export const getDealerLocation = (
  dealerCoords: { lat: number; lng: number } | null,
  user: any
): LocationCoords | null => {
  if (dealerCoords && dealerCoords.lat != null && dealerCoords.lng != null && dealerCoords.lat !== 0 && dealerCoords.lng !== 0) {
    return { lat: dealerCoords.lat, lng: dealerCoords.lng };
  }
  return null;
};

export const getDealerCity = (user: any): string | null => {
  if (user?.city) return user.city;
  if (user?.citta) return user.citta;
  return null;
};

export const getActiveLocation = (
  user: any,
  dealerCoords: { lat: number; lng: number } | null,
  location: { lat: number; lng: number } | null,
  useLocation: boolean,
  useFixedLocation: boolean
): LocationCoords => {
  const isAdmin = user?.is_admin === true;
  const dealerLoc = getDealerCoords(user);
  const dealerCity = getDealerCity(user);

  // 1) Dealer/admin profile coordinates
  if (dealerLoc) {
    const locationName = dealerCity || `${(Number(dealerLoc.lat) || 0).toFixed(4)},${(Number(dealerLoc.lng) || 0).toFixed(4)}`;
    return { lat: dealerLoc.lat, lng: dealerLoc.lng, source: `Mio Negozio: ${locationName}` };
  }

  // 2) Admin fallback Lecce
  if (isAdmin) {
    return { lat: ADMIN_LECCE_COORDS.lat, lng: ADMIN_LECCE_COORDS.lng, source: 'Lecce (Admin)' };
  }

  // 3) GPS/Manual selection
  if (useLocation && location?.lat && location?.lng) {
    return { lat: location.lat, lng: location.lng, source: 'GPS' };
  }
  if (location?.lat && location?.lng) {
    return { lat: location.lat, lng: location.lng, source: 'Selezionata' };
  }

  // 4) Default fallback
  if (useFixedLocation) {
    return { lat: ADMIN_LECCE_COORDS.lat, lng: ADMIN_LECCE_COORDS.lng, source: 'Lecce' };
  }

  return { lat: ADMIN_LECCE_COORDS.lat, lng: ADMIN_LECCE_COORDS.lng, source: 'Default' };
};

export const getDefaultItemsPerPage = (cols: number): number => {
  switch (cols) {
    case 2: return 24;
    case 3: return 24;
    case 4: return 24;
    default: return 24;
  }
};

export const hasActiveFilters = (filters: ItemFilters): boolean => {
  return (
    filters.brand !== '' ||
    filters.model !== '' ||
    filters.min_price !== '' ||
    filters.max_price !== '' ||
    filters.year !== '' ||
    filters.min_length !== '' ||
    filters.max_length !== '' ||
    filters.engine_type !== '' ||
    filters.fuel_type !== '' ||
    filters.condition !== '' ||
    filters.min_hours !== '' ||
    filters.max_hours !== '' ||
    filters.horsepower !== '' ||
    filters.boat_type_code !== ''
  );
};

export const getActiveFiltersCount = (
  filters: ItemFilters,
  useLocation: boolean,
  useFixedLocation: boolean,
  location: { lat: number; lng: number } | null,
  availableOnly: boolean,
  statusFilter: string
): number => {
  let count = 0;
  if (filters.brand) count++;
  if (filters.model) count++;
  if (filters.min_price) count++;
  if (filters.max_price) count++;
  if (filters.year) count++;
  if (filters.min_length) count++;
  if (filters.max_length) count++;
  if (filters.engine_type) count++;
  if (filters.fuel_type) count++;
  if (filters.condition) count++;
  if (filters.min_hours) count++;
  if (filters.max_hours) count++;
  if (filters.horsepower) count++;
  if (useLocation) count++;
  if (useFixedLocation) count++;
  if (location) count++;
  if (availableOnly) count++;
  if (statusFilter) count++;
  return count;
};

export const resetFilters = (): ItemFilters => {
  return { ...DEFAULT_FILTERS };
};

export const sortItemsByDistance = (
  items: any[],
  activeLocation: LocationCoords | null
): any[] => {
  if (!activeLocation) return items;
  
  return [...items].sort((a, b) => {
    const itemLatA = a.lat || a.dealer_lat;
    const itemLngA = a.lng || a.dealer_lng;
    const itemLatB = b.lat || b.dealer_lat;
    const itemLngB = b.lng || b.dealer_lng;
    
    const distA = itemLatA && itemLngA
      ? calcDistance(activeLocation.lat, activeLocation.lng, itemLatA, itemLngA)
      : 999999;
    const distB = itemLatB && itemLngB
      ? calcDistance(activeLocation.lat, activeLocation.lng, itemLatB, itemLngB)
      : 999999;
    
    return distA - distB;
  });
};